import React from 'react';
type VialCategory = 'Healing' | 'GLP-1' | 'Growth hormone' | 'Cosmetic' | 'Nootropic' | 'Anti-aging';
type Theme = {
  cap: string;
  capEdgeLight: string;
  capEdgeDark: string;
  capRidge: string;
  septum: string;
  septumHighlight: string;
  flange: string;
  cakeTop: string;
  cakeRidge: string;
  cakeBase: string;
  accentMode: 'primary' | 'foreground'; // brand divider color mode
};
const THEMES: Record<VialCategory, Theme> = {
  // Warm copper cap, cream cake — wound healing peptides (BPC, TB-500)
  Healing: {
    cap: '#B8845F',
    capEdgeLight: '#D4A57E',
    capEdgeDark: '#8B5E3B',
    capRidge: '#8E5F3B',
    septum: '#3A2418',
    septumHighlight: '#5C3A28',
    flange: '#C99478',
    cakeTop: '#FBF6EE',
    cakeRidge: '#F2EBDA',
    cakeBase: '#E8DFC8',
    accentMode: 'primary'
  },
  // Cool slate cap, near-white cake — GLP-1 receptor agonists
  'GLP-1': {
    cap: '#7A8A95',
    capEdgeLight: '#9CABB6',
    capEdgeDark: '#566570',
    capRidge: '#5B6873',
    septum: '#2A2F35',
    septumHighlight: '#454C54',
    flange: '#8A9AA5',
    cakeTop: '#FAFCFB',
    cakeRidge: '#EEF2F1',
    cakeBase: '#DEE4E2',
    accentMode: 'primary'
  },
  // Silver / aluminum cap (current default look) — GH-axis peptides
  'Growth hormone': {
    cap: '#9CA3A6',
    capEdgeLight: '#B8BCC0',
    capEdgeDark: '#6E7378',
    capRidge: '#7A8084',
    septum: '#3A3F44',
    septumHighlight: '#5A6066',
    flange: '#A8ACB0',
    cakeTop: '#FBFAF6',
    cakeRidge: '#F5F2EA',
    cakeBase: '#EEEAE0',
    accentMode: 'primary'
  },
  // Muted rose cap — cosmetic / dermal peptides
  Cosmetic: {
    cap: '#C49C8A',
    capEdgeLight: '#DDB8A6',
    capEdgeDark: '#956F5E',
    capRidge: '#9B7565',
    septum: '#3A2924',
    septumHighlight: '#5A3F36',
    flange: '#D4AC9A',
    cakeTop: '#FBF4F2',
    cakeRidge: '#F2E5E0',
    cakeBase: '#E5D2CA',
    accentMode: 'primary'
  },
  // Deep teal / steel cap — cognitive peptides
  Nootropic: {
    cap: '#5A6F7A',
    capEdgeLight: '#7C8E97',
    capEdgeDark: '#3D4E58',
    capRidge: '#404F58',
    septum: '#1F2A30',
    septumHighlight: '#3A464E',
    flange: '#6A7F8A',
    cakeTop: '#F5F7F8',
    cakeRidge: '#E8ECEE',
    cakeBase: '#D5DBDE',
    accentMode: 'primary'
  },
  // Muted plum cap — anti-aging / longevity peptides
  'Anti-aging': {
    cap: '#7E6D88',
    capEdgeLight: '#9D8DA8',
    capEdgeDark: '#5A4D63',
    capRidge: '#5C4E66',
    septum: '#2A1F30',
    septumHighlight: '#453A50',
    flange: '#8E7D98',
    cakeTop: '#F8F6FB',
    cakeRidge: '#EDE7F3',
    cakeBase: '#DCD3E5',
    accentMode: 'primary'
  }
};
type Props = {
  label?: string;
  size?: number; // height in px
  className?: string;
  showHighlight?: boolean;
  showShadow?: boolean;
  doseMg?: number;
  volumeMl?: number;
  category?: VialCategory;
};
/**
 * Custom SVG illustration of a research peptide vial.
 * Flat-editorial style — solid colors only, no gradients.
 *
 * Per-category theming: the aluminum crimp cap, septum, and lyophilised cake
 * tint shift subtly per product category so the catalogue reads as a family
 * of distinct vials rather than identical bottles with different labels.
 */
export function Vial({
  label = 'BPC-157',
  size = 280,
  className = '',
  showHighlight = true,
  showShadow = true,
  doseMg = 5,
  volumeMl = 10,
  category = 'Growth hormone'
}: Props) {
  const t = THEMES[category] ?? THEMES['Growth hormone'];
  const width = size * 0.5;
  return <svg viewBox="0 0 140 280" width={width} height={size} className={className} aria-label={`Research peptide vial${label ? ` — ${label}` : ''}`} role="img" data-id="element-758">
      {/* Soft ground shadow */}
      {showShadow && <ellipse cx="70" cy="272" rx="48" ry="4" fill="currentColor" opacity="0.08" data-id="element-759" />}

      {/* Aluminum crimp cap */}
      <rect x="40" y="10" width="60" height="22" rx="2" fill={t.cap} data-id="element-760" />
      {/* Crimp lines */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map(i => <rect key={i} x={42 + i * 7.4} y="14" width="1.2" height="14" fill={t.capRidge} opacity="0.55" data-id="element-761" />)}
      <rect x="40" y="10" width="60" height="2" fill={t.capEdgeLight} data-id="element-762" />
      <rect x="40" y="30" width="60" height="2" fill={t.capEdgeDark} data-id="element-763" />

      {/* Rubber septum */}
      <rect x="56" y="6" width="28" height="8" rx="1.5" fill={t.septum} data-id="element-764" />
      <rect x="56" y="6" width="28" height="1.5" fill={t.septumHighlight} opacity="0.7" data-id="element-765" />

      {/* Cap-to-bottle flange */}
      <rect x="38" y="32" width="64" height="4" rx="1" fill={t.flange} data-id="element-766" />

      {/* Glass vial body */}
      <rect x="38" y="36" width="64" height="220" rx="4" fill="#EEF2F1" data-id="element-767" />
      <rect x="38" y="36" width="4" height="220" rx="2" fill="#D9DFDE" opacity="0.6" data-id="element-768" />
      <rect x="98" y="36" width="4" height="220" rx="2" fill="#D9DFDE" opacity="0.6" data-id="element-769" />

      {/* Lyophilised cake */}
      <rect x="42" y="200" width="56" height="54" rx="2" fill={t.cakeTop} data-id="element-770" />
      <path d="M 42 204 Q 50 199 58 202 T 74 201 T 90 203 T 98 202 L 98 210 L 42 210 Z" fill={t.cakeRidge} data-id="element-771" />
      <rect x="42" y="250" width="56" height="6" fill={t.cakeBase} data-id="element-772" />

      {/* Glass highlight stripes */}
      {showHighlight && <rect x="46" y="42" width="2.5" height="150" rx="1" fill="#FFFFFF" opacity="0.6" data-id="element-773" />}
      {showHighlight && <rect x="50" y="42" width="1" height="100" rx="0.5" fill="#FFFFFF" opacity="0.35" data-id="element-774" />}

      {/* Label band */}
      <rect x="38" y="100" width="64" height="64" fill="#FFFFFF" data-id="element-775" />
      <rect x="38" y="100" width="64" height="0.75" fill="#E5E7E6" data-id="element-776" />
      <rect x="38" y="164" width="64" height="0.75" fill="#E5E7E6" data-id="element-777" />

      {/* Brand wordmark — top of label */}
      <text x="70" y="112" textAnchor="middle" fontFamily="Geist, system-ui, sans-serif" fontSize="7.5" fontWeight="700" letterSpacing="-0.15" data-id="element-778">
        <tspan fill="#0F1410" data-id="element-779">peptides</tspan>
        <tspan className="fill-primary" data-id="element-780">.ml</tspan>
      </text>

      {/* Brand divider */}
      <rect x="58" y="117" width="24" height="0.75" rx="0.5" className="fill-primary" opacity="0.6" data-id="element-781" />

      {/* Product name */}
      <text x="70" y="135" textAnchor="middle" fontFamily="Geist, system-ui, sans-serif" fontSize="11" fontWeight="600" fill="#0F1410" letterSpacing="-0.2" data-id="element-782">
        {label}
      </text>

      {/* Spec line — mg + ml in primary green */}
      <text x="70" y="149" textAnchor="middle" fontFamily="Geist Mono, ui-monospace, monospace" fontSize="6.5" fontWeight="500" letterSpacing="0.5" data-id="element-783">
        <tspan fill="#0F1410" data-id="element-784">{doseMg}</tspan>
        <tspan className="fill-primary" fontWeight="600" data-id="element-785">
          MG
        </tspan>
        <tspan fill="#6B7270" data-id="element-786"> · </tspan>
        <tspan fill="#0F1410" data-id="element-787">{volumeMl}</tspan>
        <tspan className="fill-primary" fontWeight="600" data-id="element-788">
          ML
        </tspan>
      </text>

      {/* Bottle base rim */}
      <rect x="38" y="252" width="64" height="4" rx="2" fill="#D9DFDE" data-id="element-789" />
    </svg>;
}